/**
 * Pieces the Expenses screens share: the house button and field styles, the
 * modal and side-panel shells, receipts (thumbnails, viewer, drop zone) and the
 * client's submission-notes editor.
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from "react";

import { useToast } from "@/context/ToastContext";
import { useClientMutations } from "@/hooks/useTime";
import { receiptUrl } from "@/hooks/useExpenses";
import { appMode } from "@/lib/pocketbase";
import { isPdf } from "@/lib/expenses";
import { pdfPages } from "@/lib/pdfPages";

export { HeaderTabs } from "@/components/HeaderTabs";
import type { Client, Expense } from "@/lib/types";

// ---- Styles ----------------------------------------------------------------

export const primaryBtn: CSSProperties = {
  padding: "8px 16px", borderRadius: 999, border: "none",
  background: "var(--accent)", color: "var(--onAccent)", fontSize: 12.5, cursor: "pointer", whiteSpace: "nowrap",
};
export const ghostBtn: CSSProperties = {
  padding: "7px 14px", borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 12.5, cursor: "pointer", whiteSpace: "nowrap",
};
export const dangerBtn: CSSProperties = { ...ghostBtn, color: "var(--bad)", borderColor: "var(--line2)" };
export const linkBtn: CSSProperties = {
  border: "none", background: "none", padding: 0, color: "var(--accent)", fontSize: 12, cursor: "pointer",
};
export const label: CSSProperties = { display: "block", fontSize: 11.5, color: "var(--muted)", marginBottom: 12 };
export const input: CSSProperties = {
  width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 13, marginTop: 5,
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
};
export const th: CSSProperties = {
  textAlign: "left", fontSize: 11, fontWeight: 500, color: "var(--muted)", textTransform: "uppercase",
  letterSpacing: "0.4px", padding: "8px 10px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap",
};
export const td: CSSProperties = { padding: "10px", fontSize: 13, borderBottom: "1px solid var(--tint2)", verticalAlign: "middle" };

// ---- Shells ----------------------------------------------------------------

export function Modal({ title, width = 460, onClose, children }: {
  title: string; width?: number; onClose: () => void; children: ReactNode;
}) {
  useEscape(onClose);
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "var(--scrim)", zIndex: 50,
      display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "60px 20px", overflowY: "auto",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width, maxWidth: "100%", background: "var(--raised)", borderRadius: 14,
        padding: "24px 26px", border: "1px solid var(--overlayLine)", boxShadow: "var(--shadowOverlay)",
      }}>
        <h2 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 500 }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

/** A panel from the right, so the report stays in view beside the item being edited. */
export function SidePanel({ title, onClose, footer, children }: {
  title: string; onClose: () => void; footer?: ReactNode; children: ReactNode;
}) {
  useEscape(onClose);
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "var(--scrim)", zIndex: 50 }} />
      <aside style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: 440, maxWidth: "100vw", zIndex: 51,
        background: "var(--raised)", borderLeft: "1px solid var(--overlayLine)", boxShadow: "var(--shadowOverlay)",
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ display: "flex", alignItems: "center", padding: "18px 22px", borderBottom: "1px solid var(--line)" }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 500, flex: 1 }}>{title}</h2>
          <button onClick={onClose} aria-label="Close" style={{ ...linkBtn, color: "var(--muted)", fontSize: 18 }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}>{children}</div>
        {footer && (
          // The demo badge sits in the bottom-right corner; clear it rather than
          // stack over it, since toasts must stay above this panel.
          <div style={{ display: "flex", gap: 8, padding: `14px 22px ${appMode === "demo" ? 58 : 14}px`, borderTop: "1px solid var(--line)" }}>{footer}</div>
        )}
      </aside>
    </>
  );
}

function useEscape(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
}

export function Toggle({ on, onChange, children }: { on: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "var(--ink2)", padding: "7px 0", cursor: "pointer" }}>
      <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: "var(--accent)", width: 15, height: 15 }} />
      {children}
    </label>
  );
}

export function Segmented<T extends string>({ value, options, onChange }: {
  value: T; options: [T, string][]; onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: "inline-flex", gap: 3, padding: 3, background: "var(--tint)", borderRadius: 999, marginTop: 5 }}>
      {options.map(([v, l]) => {
        const on = v === value;
        return (
          <button key={v} type="button" onClick={() => onChange(v)} style={{
            padding: "5px 14px", borderRadius: 999, fontSize: 12.5, cursor: "pointer",
            border: on ? "1px solid var(--accentLine)" : "1px solid transparent",
            background: on ? "var(--surface)" : "transparent",
            color: on ? "var(--accent)" : "var(--ink3)", fontWeight: on ? 500 : 400,
          }}>{l}</button>
        );
      })}
    </div>
  );
}

/**
 * One of a row's R / Q / I squares. With `onToggle` it is a button that flips
 * the flag in place, without opening the item; the click stops there, so the
 * row underneath doesn't open as well.
 */
export function Flag({ on, title, children, onToggle }: {
  on: boolean; title: string; children: ReactNode; onToggle?: () => void;
}) {
  const style: CSSProperties = {
    display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18, height: 18,
    borderRadius: 5, fontSize: 10.5, padding: 0, border: "none",
    background: on ? "var(--goodTint)" : "var(--warnTint)", color: on ? "var(--goodInk)" : "var(--warn)",
  };
  if (!onToggle) return <span title={title} className="mono" style={style}>{children}</span>;
  return (
    <button
      type="button" className="mono expense-flag" aria-pressed={on} title={`${title} — click to change`}
      onClick={(ev) => { ev.stopPropagation(); onToggle(); }}
      style={{ ...style, cursor: "pointer" }}
    >{children}</button>
  );
}

// ---- Receipts --------------------------------------------------------------

const ACCEPT = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic"];
export const RECEIPT_ACCEPT = "application/pdf,image/jpeg,image/png,image/webp,image/heic";
/** PocketBase's own limit on the field: 10 MB a file. */
const MAX_BYTES = 10 * 1024 * 1024;

/** Keep what the receipts field will accept, and say what it will not. */
export function acceptableReceipts(files: FileList | File[], toast: (m: string) => void): File[] {
  const out: File[] = [];
  for (const f of Array.from(files)) {
    if (!ACCEPT.includes(f.type) && !isPdf(f.name)) { toast(`${f.name}: only PDFs and images`); continue; }
    if (f.size > MAX_BYTES) { toast(`${f.name} is over 10 MB`); continue; }
    out.push(f);
  }
  return out;
}

/** Drag-and-drop target. `active` is true while files are over it. */
export function useDropZone(onFiles: (files: File[]) => void, enabled = true) {
  const [active, setActive] = useState(false);
  const files = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes("Files");
  return {
    active,
    props: {
      onDragOver: (e: React.DragEvent) => {
        if (!files(e)) return;
        // Always claimed, even when off: an unclaimed file drop makes the
        // browser open the file in place of the app.
        e.preventDefault();
        if (!enabled) { e.dataTransfer.dropEffect = "none"; return; }
        e.stopPropagation();
        setActive(true);
      },
      onDragLeave: (e: React.DragEvent) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        setActive(false);
      },
      // The drop bubbles on so an enclosing zone also clears its highlight;
      // the innermost zone claims the files and the outer ones skip them.
      onDrop: (e: React.DragEvent) => {
        if (!files(e)) return;
        e.preventDefault(); setActive(false);
        const ev = e.nativeEvent as DragEvent & { dropClaimed?: boolean };
        if (ev.dropClaimed || !enabled) return;
        ev.dropClaimed = true;
        onFiles(Array.from(e.dataTransfer.files));
      },
    },
  };
}

export function ReceiptThumb({ expense, name, onOpen, onRemove }: {
  expense: Expense; name: string; onOpen: () => void; onRemove?: () => void;
}) {
  const url = receiptUrl(expense, name);
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <button type="button" onClick={onOpen} title={name} style={{ border: "none", background: "none", padding: 0, cursor: "pointer", display: "block" }}>
        {isPdf(name) ? (
          <span style={{
            width: 72, height: 72, borderRadius: 10, border: "1px solid var(--line)", background: "var(--tint)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3,
            fontSize: 10, fontWeight: 600, color: "var(--ink3)",
          }}>PDF<span style={{ fontWeight: 400, maxWidth: 64, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span></span>
        ) : (
          <img src={url} alt={name} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 10, border: "1px solid var(--line)", display: "block" }} />
        )}
      </button>
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label="Remove receipt" style={{
          position: "absolute", top: -6, right: -6, width: 22, height: 22, borderRadius: "50%", border: "none",
          background: "var(--ink)", color: "var(--bg)", cursor: "pointer", lineHeight: 1, fontSize: 13,
        }}>×</button>
      )}
    </span>
  );
}

/** Full-size receipt over the app. PDFs are rendered to page images. */
export function ReceiptViewer({ url, name, onClose }: { url: string; name: string; onClose: () => void }) {
  useEscape(onClose);
  const pdf = isPdf(name);
  const [pages, setPages] = useState<string[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!pdf) return;
    let live = true;
    pdfPages(url).then((p) => { if (live) setPages(p); }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [pdf, url]);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 80, background: "rgba(10,12,14,0.82)",
      display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 24px 16px",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{ flex: 1, minHeight: 0, overflowY: "auto", maxWidth: 900, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        {!pdf && <img src={url} alt={name} style={{ maxWidth: "100%", borderRadius: 6, background: "#fff" }} />}
        {pdf && pages?.map((src, i) => <img key={i} src={src} alt={`${name} page ${i + 1}`} style={{ maxWidth: "100%", borderRadius: 6, background: "#fff" }} />)}
        {pdf && !pages && !failed && <div style={{ color: "#fff", fontSize: 13, marginTop: 40 }}>Rendering PDF…</div>}
        {pdf && failed && <div style={{ color: "#fff", fontSize: 13, marginTop: 40 }}>Couldn't render this PDF.</div>}
      </div>
      <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", gap: 16, alignItems: "center", color: "#fff", fontSize: 12.5, marginTop: 12 }}>
        <span style={{ opacity: 0.8 }}>{name}</span>
        <a href={url} target="_blank" rel="noopener noreferrer" download={name} style={{ color: "#fff" }}>Open original</a>
        <button onClick={onClose} style={{ ...ghostBtn, background: "transparent", color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}>Close</button>
      </div>
    </div>
  );
}

// ---- Client submission notes ----------------------------------------------

/**
 * The client's submission notes, editable where they are used. They live on
 * the client — one note per client, printed on every package for it — which
 * the label says, so an edit here is not mistaken for a note on one report.
 */
export function ClientNotes({ client, compact }: { client: Client | undefined; compact?: boolean }) {
  const { saveClient } = useClientMutations();
  const toast = useToast();
  const [draft, setDraft] = useState<string | null>(null);
  if (!client) return null;
  const text = client.reimbursementNotes ?? "";

  async function save() {
    try {
      await saveClient.mutateAsync({ id: client!.id, reimbursementNotes: (draft ?? "").trim() });
      toast("Submission notes saved");
      setDraft(null);
    } catch {
      toast("Couldn't save the notes");
    }
  }

  return (
    <div style={{
      fontSize: 12.5, background: "var(--tint)", border: "1px solid var(--line)", borderRadius: 8,
      padding: compact ? "8px 10px" : "10px 12px",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontWeight: 500, color: "var(--ink2)" }}>Submission notes</span>
        {draft === null && <button onClick={() => setDraft(text)} style={{ ...linkBtn, marginLeft: "auto" }}>{text ? "Edit" : "Add"}</button>}
      </div>
      <div style={{ color: "var(--muted)", fontSize: 11.5, marginBottom: 5 }}>
        Kept on {client.name} and printed on every {client.name} report.
      </div>
      {draft === null ? (
        <div style={{ color: text ? "var(--ink3)" : "var(--muted2)", whiteSpace: "pre-wrap" }}>
          {text || "None — add where to send the report, forms to include, or limits to respect."}
        </div>
      ) : (
        <>
          <textarea autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            style={{ ...input, minHeight: 70, resize: "vertical" }}
            placeholder="e.g. Email to ap@client.com with the travel form attached." />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <button onClick={() => setDraft(null)} style={ghostBtn}>Cancel</button>
            <button onClick={() => void save()} style={primaryBtn} disabled={saveClient.isPending}>Save notes</button>
          </div>
        </>
      )}
    </div>
  );
}
